if (!customElements.get('infinity-slider')) {
  class InfinitySlider extends HTMLElement {
    constructor() {
      super();
      this.setupInfinitySliderMobile();
    }

    setupInfinitySliderMobile() {
      if (window.innerWidth < 750 || screen.width < 750) {
        this.initializeElements();
        this.initializeState();
        this.setBasePosition();
        this.addEventListeners();
      }
    }

    initializeElements() {
      this.track = this.querySelector('.infinity-slider__track');
      this.cloneTrack = this.track.cloneNode(true); // Clone track for infinite effect
      this.slideWidth = this.track.firstElementChild.getBoundingClientRect().width;
      this.dots = this.querySelectorAll('.infinity-slider__dot');
      this.maxIndex = this.track.children.length - 1;
    }

    initializeState() {
      this.currentSlide = 0;
      this.currentIndex = 0;
      this.startX = 0;
      this.endX = 0;
    }

    addEventListeners() {
      this.setupPaginationSlider();
      this.setupSwipeSlider();
    }

    setupSwipeSlider() {
      this.track.addEventListener('touchstart', (event) => {
        this.startX = event.touches[0].clientX;
      });

      this.track.addEventListener('touchmove', (event) => {
        this.endX = event.touches[0].clientX;
      });

      this.track.addEventListener('touchend', () => {
        const deltaX = this.startX - this.endX;
        if (Math.abs(deltaX) > 50) { // Swipe threshold
          this.moveToSlide(deltaX > 0 ? 'next' : 'prev');
        }
      });
    }

    setupPaginationSlider() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const targetIndex = index;
          const currentIndex = this.currentIndex;
          const direction = currentIndex < targetIndex ? 'next' : 'prev';
          const steps = Math.abs(targetIndex - currentIndex);

          // Function to move slides recursively with a slight delay for animations
          const moveSlides = (step) => {
            if (step > 0) {
              this.moveToSlide(direction);
              setTimeout(() => moveSlides(step - 1), 400); // Adjust delay as needed
            } else {
              this.updateActiveDot();
            }
          };

          moveSlides(steps);
        });
      });
    }

    moveToSlide(direction) {
      if (direction === 'next') {
        this.currentSlide++;
        this.handleMoveSlider(direction);
        this.currentIndex++;
        this.currentIndex = this.setupInfinityLoop(this.currentIndex);
      } else {
        this.currentSlide--;
        this.currentIndex--;
        this.currentIndex = this.setupInfinityLoop(this.currentIndex);
        this.handleMoveSlider(direction);
      }
      this.updateActiveDot();
    }

    handleMoveSlider(direction) {
      this.track.style.transform = `translate3d(${(-this.currentSlide * this.slideWidth)}px, 0, 0)`;
      this.updateItems(direction);
    }

    updateItems(direction) {
      const cloneSlide = [...this.cloneTrack.children][this.currentIndex].cloneNode(true);
      if (direction === 'next') {
        this.appendCloneSlide(cloneSlide);
      } else {
        this.prependCloneSlide(cloneSlide);
      }
    }

    appendCloneSlide(cloneSlide) {
      cloneSlide.style.transform = `translate3d(${((this.maxIndex + this.currentSlide) * this.slideWidth)}px, 0, 0)`;
      this.track.append(cloneSlide);
      setTimeout(() => this.track.firstElementChild.remove(), 400);
    }

    prependCloneSlide(cloneSlide) {
      cloneSlide.style.transform = `translate3d(${(this.currentSlide * this.slideWidth)}px, 0, 0)`;
      this.track.prepend(cloneSlide);
      setTimeout(() => this.track.lastElementChild.remove(), 400);
    }

    setBasePosition() {
      [...this.track.children].forEach((elem, index) => {
        elem.style.transform = `translate3d(${(this.slideWidth * index)}px, 0, 0)`;
      });
    }

    setupInfinityLoop(currentIndex) {
      return (currentIndex + this.maxIndex + 1) % (this.maxIndex + 1);
    }

    updateActiveDot() {
      this.dots.forEach((dot, index) => {
        dot.toggleAttribute('data-active', index === this.currentIndex);
      });
    }
  }

  customElements.define('infinity-slider', InfinitySlider);
}

if (!customElements.get('comparative-slider')) {
  class ComparativeSlider extends HTMLElement {
    constructor() {
      super();
      // Query selectors for necessary elements
      this.cardText = this.querySelectorAll('.comparative-slider__txt');
      this.beforeElement = this;
      this.afterElement = this.querySelector('[data-after]');
      this.divider = this.querySelector('.comparative-slider__divider');
      this.isDragging = false;

      // Event listeners for dragging functionality (Mouse and Touch) on the divider
      this.divider.addEventListener('mousedown', this.startDrag.bind(this));
      this.divider.addEventListener('mousemove', this.onDrag.bind(this));
      this.divider.addEventListener('mouseup', this.stopDrag.bind(this));

      // Touch events for mobile support on the divider
      this.divider.addEventListener('touchstart', this.startDrag.bind(this));
      this.divider.addEventListener('touchmove', this.onDrag.bind(this));
      this.divider.addEventListener('touchend', this.stopDrag.bind(this));

      // Initial setup
      this.compareElements();
    }

    // Initial setup of the divider position
    compareElements() {
      this.updateDivider(50); // Center the divider at 50%
    }

    // Starts the dragging process (Handles both mouse and touch)
    startDrag(e) {
      e.stopPropagation();
      this.isDragging = true;
      this.onDrag(e); // Update immediately on mousedown/touchstart
    }

    // Handles the dragging movement (Supports both mouse and touch)
    onDrag(e) {
      e.stopPropagation();
      if (this.isDragging) {
        let clientX;

        // Determine the clientX position based on mouse or touch event
        if (e.type === 'mousemove') {
          clientX = e.clientX;
        } else if (e.type === 'touchmove') {
          clientX = e.touches[0].clientX;
        }

        if (clientX) {
          // Get the bounding rectangle of the before element
          const rect = this.beforeElement.getBoundingClientRect();
          // Calculate the mouse/touch position relative to the element
          const positionX = clientX - rect.left;
          const width = rect.width;
          // Calculate the percentage based on the position
          const percentage = (positionX / width) * 100;

          // Limit the percentage to stay between 0% and 100%
          const clampedPercentage = Math.max(0, Math.min(percentage, 100));

          // Update the clip-path of the after element based on the percentage
          this.afterElement.style.clipPath = `inset(0 ${100 - clampedPercentage}% 0 0)`;
          // Update the divider position based on the percentage
          this.updateDivider(clampedPercentage);
          // Check and update the visibility of text elements
          this.checkTextVisibility(clampedPercentage);
        }
      }
    }

    // Stops the dragging process (Handles both mouse and touch)
    stopDrag(e) {
      e.stopPropagation();
      this.isDragging = false;
    }

    // Updates the position of the divider
    updateDivider(percentage) {
      this.divider.style.left = `${percentage}%`;
    }

    // Checks if the divider overlaps any text elements and updates their data-active attribute
    checkTextVisibility(percentage) {
      // Calculate the position of the divider based on the percentage
      const dividerPosition = (percentage / 100) * this.beforeElement.clientWidth;

      this.cardText.forEach(textElement => {
        // Get the bounding rectangle of the text element and the before element
        const textRect = textElement.getBoundingClientRect();
        const elementRect = this.beforeElement.getBoundingClientRect();

        // Check if the divider overlaps the text element
        const isOverlapping =
          (textRect.left < elementRect.left + dividerPosition &&
            textRect.right > elementRect.left + dividerPosition);

        // Update the data-active attribute based on overlap
        textElement.setAttribute('data-active', !isOverlapping);
      });
    }
  }

  // Define the custom element
  customElements.define('comparative-slider', ComparativeSlider);
}
